import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { IMaskInput } from 'react-imask';
import { useTheme } from '../../context/ThemeContext';
import Button from '../UI/Button';
import { StepProps } from '../../types';

interface Step2Data {
  email_address: string;
  phone_number: string;
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const Title = styled.h2.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

const Description = styled.p.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  font-size: 0.875rem;
`;

const ErrorMessage = styled.span.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  color: ${props => props.theme.colors.error};
  font-size: 0.75rem;
  font-weight: 500;
`;

const InfoMessage = styled.span.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop),
})<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.75rem;
  font-style: italic;
  opacity: 0.8;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Step2PersonalInfo: React.FC<StepProps> = ({ data, onNext, onPrevious }) => {
  const { theme } = useTheme();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<Step2Data>({
    defaultValues: {
      email_address: data.email_address || '',
      phone_number: data.phone_number || '',
    },
  });

  const onSubmit = (formData: Step2Data) => {
    onNext(formData);
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <HeaderContainer>
        <Title theme={theme}>Personal Information</Title>
        <Description theme={theme}>
          Please provide your personal contact information and identification details.
        </Description>
      </HeaderContainer>

      <InputGroup>
        <Label theme={theme}>
          Email Address <span style={{ color: theme.colors.error }}>*</span>
        </Label>
        <input
          type="email"
          placeholder="Enter your email address"
          style={{
            padding: '0.75rem',
            border: `2px solid ${errors.email_address ? theme.colors.error : theme.colors.border}`,
            borderRadius: '8px',
            fontSize: '1rem',
            fontFamily: 'Raleway, sans-serif',
            backgroundColor: theme.colors.background,
            color: theme.colors.text,
            transition: 'all 0.3s ease',
          }}
          {...register('email_address', {
            required: 'Email address is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Please enter a valid email address',
            },
          })}
        />
        {errors.email_address && (
          <ErrorMessage theme={theme}>{errors.email_address.message}</ErrorMessage>
        )}
      </InputGroup>

      <InputGroup>
        <Label theme={theme}>
          Phone Number <span style={{ color: theme.colors.error }}>*</span>
        </Label>
        <Controller
          name="phone_number"
          control={control}
          rules={{
            required: 'Phone number is required',
            validate: (value) => {
              const cleanValue = value?.trim() || '';
              if (!cleanValue) {
                return 'Phone number is required';
              }
              if (cleanValue.length !== 11) {
                return 'Phone number must be exactly 11 digits';
              }
              if (!/^\d{11}$/.test(cleanValue)) {
                return 'Phone number must contain digits only';
              }
              return true;
            },
          }}
          render={({ field }) => (
            <IMaskInput
              mask="00000000000"
              placeholder="08012345678"
              lazy={false}
              value={field.value}
              onAccept={(value) => {
                field.onChange(value);
              }}
              style={{
                padding: '0.75rem',
                border: `2px solid ${errors.phone_number ? theme.colors.error : theme.colors.border}`,
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Raleway, sans-serif',
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                transition: 'all 0.3s ease',
              }}
            />
          )}
        />
        <InfoMessage theme={theme}>Format: 11 digits (e.g., 08012345678)</InfoMessage>
        {errors.phone_number && (
          <ErrorMessage theme={theme}>{errors.phone_number.message}</ErrorMessage>
        )}
      </InputGroup>

      {/* National Identification Number (NIN) removed per updated flow */}

      <ButtonContainer>
        <Button type="button" variant="outline" onClick={onPrevious}>
          Previous
        </Button>
        <Button type="submit" size="large">
          Next Step
        </Button>
      </ButtonContainer>
    </Form>
  );
};

export default Step2PersonalInfo;
